import request from 'utils/request'
import { combineReducers } from 'redux'
import moment from 'moment'
import { loadingActions } from 'store/loader'
import {
  formatEventBody,
  formatInstanceEventBody,
  formatVisibleInstancesEventBody,
  formatAllInstancesEventBody
} from 'utils/events'
import {
  EVENT_KEY_MAP,
  INSTRUMENT_EVENTS_MAP,
  DEFAULT_INSTANCE_VISIBLE_DAYS,
  WEB_SUPPORTED_INSTRUMENT_TYPES,
  INSTRUMENT_DISPLAY_LOCATION_OPTIONS,
} from 'utils/constants'
import { setAnnouncements } from '../../Announcements/modules/Announcements'
import {
  normalizeVisibilityConfig,
  isVisibleByConfig,
  isVisibleOnCalendarDay,
} from '../utils/expirationUtils'
import {
  isWithinInstanceVisibilityWindow,
  isInstanceActiveInCurrentWindow,
  isPersistentInstrumentGroup,
} from '../utils/instrumentUtils'
import {
  getParticipantFirstLogin,
  getScheduleDeploymentDay,
  hasAbsoluteDeploy,
  hasNowDeploy,
  hasActiveInstanceVisibleOnCalendarDay,
  isAbsoluteDeployScheduleVisibleOnDay,
  isNowDeployScheduleVisibleOnDay,
  isInstanceVisibleOnCalendarDay,
  isMultiScheduleInstanceActiveAt,
  isPersistentRecurringScheme,
  shouldHideUnconfirmedVisitInstrument,
  shouldHideNonPersistentPendingOnDay,
  shouldShowPersistentOrVisitInstrumentOnDay,
} from '../utils/calendarInstrumentUtils'
import {
  flattenParticipantVisits,
  getConfirmedVisitKeySet,
} from '../../Visits/modules/visitUtils'

import packageJson from '../../../../package.json'
import { logSentryEvent, logInstrumentInstances, SENTRY_MESSAGES } from '../../../utils/sentry'
import {
  expandInstrumentsByInstances,
  resolveSchedule,
  getSchedulesList,
  getScheduleInstanceEntries,
  isMultiScheduleMetadata,
  resolveInstrumentVisibilityConfig,
  usesScheduleScopedVisibility,
  getScheduleVisibilityConfig,
  mergeInstances,
} from '../utils/instrumentUtils'

const isWebSupportedInstrument = ({ instrument_type: type } = {}) =>
  WEB_SUPPORTED_INSTRUMENT_TYPES.includes(type)

const SET_PENDING_INSTRUMENTS = 'SET_PENDING_INSTRUMENTS'
const SET_INSTRUMENTS = 'SET_INSTRUMENTS'
const SET_ALL_INSTRUMENTS = 'SET_ALL_INSTRUMENTS'
const SET_FUTURE_INSTANCES = 'SET_FUTURE_INSTANCES'
const SET_ALL_INSTRUMENTS_INSTANCES = 'SET_ALL_INSTRUMENTS_INSTANCES'
const SET_INSTRUMENTS_INSTANCES = 'SET_INSTRUMENTS_INSTANCES'
const SET_PARTICIPANT_INSTRUMENTS_INSTANCES =
  'SET_PARTICIPANT_INSTRUMENTS_INSTANCES'
const SET_CONFIRMED_VISIT_KEYS = 'SET_CONFIRMED_VISIT_KEYS'

const setPendingInstruments = instruments => {
  return {
    type: SET_PENDING_INSTRUMENTS,
    instruments
  }
}

const setInstruments = instruments => {
  return {
    type: SET_INSTRUMENTS,
    instruments
  }
}

const setAllInstruments = instruments => {
  return {
    type: SET_ALL_INSTRUMENTS,
    instruments
  }
}

const setInstrumentsInstances = instances => {
  return {
    type: SET_INSTRUMENTS_INSTANCES,
    instances
  }
}

const setAllInstrumentsInstances = instances => {
  return {
    type: SET_ALL_INSTRUMENTS_INSTANCES,
    instances
  }
}

const setFutureInstances = instances => {
  return {
    type: SET_FUTURE_INSTANCES,
    instances
  }
}

const setParticipantInstrumentsInstances = instances => {
  return {
    type: SET_PARTICIPANT_INSTRUMENTS_INSTANCES,
    instances
  }
}

const setConfirmedVisitKeys = keys => ({
  type: SET_CONFIRMED_VISIT_KEYS,
  keys
})

const fetchConfirmedVisitKeySet = studyId => dispatch => {
  if (!studyId) return Promise.resolve(new Set())

  return dispatch(
    request({
      method: 'GET',
      url: `/control/studies/${studyId}/study_schedules_visits/participants/me`,
      hasLoader: false,
      skipNotyFail: true,
      success: json =>
        getConfirmedVisitKeySet(
          flattenParticipantVisits(
            json?.ss_visits_participant,
            json?.ss_visits_participant_adhoc,
          ),
        ),
    }),
  ).then(result => (result instanceof Set ? result : new Set()))
}

export const sendInstrumentsDelivered = instruments => {
  return dispatch => {
    const depQueueIds = instruments.map(instrument =>
      String(instrument[EVENT_KEY_MAP.instrumentsDepQId])
    )
    instruments.forEach(instrument => {
      logSentryEvent({
        message: SENTRY_MESSAGES.instrumentDelivered,
        tags: {
          flow: 'instrument',
          action: 'delivered',
          instrument_id: String(
            instrument.instrument_id ||
              instrument.identity ||
              instrument[EVENT_KEY_MAP.instrumentsDepQId] ||
              '',
          ),
          instrument_type: instrument.instrument_type || instrument.type,
        },
      })
    })
    return dispatch(
      request({
        method: 'POST',
        url: '/control/participants/me/events',
        body: formatEventBody(
          INSTRUMENT_EVENTS_MAP.delivered,
          EVENT_KEY_MAP.depQId,
          depQueueIds
        ),
        success: () => { }
      })
    )
  }
}

export const sendInstrumentsVisible = instruments => {
  return dispatch => {
    const depQueueIds = instruments.map(instrument =>
      String(instrument[EVENT_KEY_MAP.instrumentsDepQId])
    )
    instruments.forEach(instrument => {
      logSentryEvent({
        message: SENTRY_MESSAGES.instrumentVisible,
        tags: {
          flow: 'instrument',
          action: 'visible',
          instrument_id: String(
            instrument.instrument_id ||
              instrument.identity ||
              instrument[EVENT_KEY_MAP.instrumentsDepQId] ||
              '',
          ),
          instrument_type: instrument.instrument_type || instrument.type,
        },
      })
    })
    return dispatch(
      request({
        method: 'POST',
        url: '/control/participants/me/events',
        body: formatEventBody(
          INSTRUMENT_EVENTS_MAP.visible,
          EVENT_KEY_MAP.depQId,
          depQueueIds
        ),
        success: () => { }
      })
    )
  }
}

export const sendEvent = (
  id,
  eventType,
  instanceID,
  isAnnouncement = false
) => {
  return (dispatch) => {
    if (!isAnnouncement) {
      if (eventType === INSTRUMENT_EVENTS_MAP.read) {
        logSentryEvent({
          message: SENTRY_MESSAGES.instrumentSeen,
          tags: {
            flow: 'instrument',
            action: 'seen',
            instrument_id: id != null ? String(id) : undefined,
            instance_id: instanceID != null ? String(instanceID) : undefined,
          },
        })
      } else if (eventType === INSTRUMENT_EVENTS_MAP.completed) {
        logSentryEvent({
          message: SENTRY_MESSAGES.instrumentCompleted,
          tags: {
            flow: 'instrument',
            action: 'completed',
            instrument_id: id != null ? String(id) : undefined,
            instance_id: instanceID != null ? String(instanceID) : undefined,
          },
        })
      } else if (eventType === INSTRUMENT_EVENTS_MAP.cancelled) {
        logSentryEvent({
          message: SENTRY_MESSAGES.instrumentCancelled,
          tags: {
            flow: 'instrument',
            action: 'cancelled',
            instrument_id: id != null ? String(id) : undefined,
            instance_id: instanceID != null ? String(instanceID) : undefined,
          },
        })
      }
    }
    const body = formatInstanceEventBody(eventType, {
      [isAnnouncement
        ? EVENT_KEY_MAP.announcementId
        : EVENT_KEY_MAP.depQId]: id,
      instance_id: instanceID
    })
    return dispatch(
      request({
        method: 'POST',
        url: '/control/participants/me/events',
        body,
        success: () => { }
      })
    )
  }
}

export const sendEventForInstances = (
  eventType,
  instancesArray,
  allInstances = false
) => (dispatch) => {
  if (eventType === INSTRUMENT_EVENTS_MAP.delivered) {
    logInstrumentInstances(
      SENTRY_MESSAGES.instrumentDelivered,
      instancesArray,
      { action: 'delivered' },
      { firstInstanceOnly: true },
    )
    logInstrumentInstances(
      SENTRY_MESSAGES.instanceDelivered,
      instancesArray,
      { action: 'instance_delivered' },
      { firstInstanceOnly: !allInstances },
    )
  } else if (eventType === INSTRUMENT_EVENTS_MAP.visible) {
    logInstrumentInstances(
      SENTRY_MESSAGES.instrumentVisible,
      instancesArray,
      { action: 'visible' },
      { firstInstanceOnly: true },
    )
  }
  const body = allInstances
    ? formatAllInstancesEventBody(eventType, instancesArray)
    : formatVisibleInstancesEventBody(eventType, instancesArray)
  return dispatch(
    request({
      method: 'POST',
      url: '/control/participants/me/events',
      body,
      success: () => { }
    })
  )
}


const getInstrumentVisibilityConfig = (instrument) => {
  if (usesScheduleScopedVisibility(instrument)) {
    return resolveInstrumentVisibilityConfig(instrument)
  }

  const { metadata } = instrument || {}
  if (!metadata) return undefined

  const topLevel = normalizeVisibilityConfig(metadata)
  if (topLevel) return topLevel

  if (typeof metadata.visibility === 'string') {
    return {
      visibility: metadata.visibility,
      visibility_ranges: metadata.visibilityRanges || [],
    }
  }

  return undefined
}

export const isInstanceVisibleByTime = (
  instrument,
  instance,
  referenceTime = moment(),
) => {
  const scheduleMeta = instrument?.metadata?.schedule

  if (isMultiScheduleMetadata(scheduleMeta) || instrument?.schedule_id) {
    const config = resolveInstrumentVisibilityConfig(instrument, instance)
    return config ? isVisibleByConfig(config, referenceTime) : true
  }

  const legacyTopLevel = getInstrumentVisibilityConfig(instrument)
  if (legacyTopLevel) {
    return isVisibleByConfig(legacyTopLevel, referenceTime)
  }

  return true
}

// Export this function so it can be used in calendar components
export const isInstrumentVisibleByTime = (instrument, referenceTime = moment()) => {
  const { metadata } = instrument
  if (!metadata) return true

  const schedule = metadata.schedule
  const instances = instrument?.instances

  if (isMultiScheduleMetadata(schedule)) {
    if (Array.isArray(instances) && instances.length) {
      return instances.some((instance) =>
        isInstanceVisibleByTime(instrument, instance, referenceTime),
      )
    }

    return getSchedulesList(schedule).some((item) => {
      const config = getScheduleVisibilityConfig(item)
      return config ? isVisibleByConfig(config, referenceTime) : true
    })
  }

  if (!usesScheduleScopedVisibility(instrument)) {
    const legacyTopLevel = getInstrumentVisibilityConfig(instrument)
    if (legacyTopLevel) {
      return isVisibleByConfig(legacyTopLevel, referenceTime)
    }
  }

  if (Array.isArray(instances) && instances.length) {
    return instances.some((instance) =>
      isInstanceVisibleByTime(instrument, instance, referenceTime),
    )
  }

  const config = resolveInstrumentVisibilityConfig(instrument)
  return config ? isVisibleByConfig(config, referenceTime) : true
}

export const isInstrumentVisibleOnCalendarDay = (
  instrument,
  targetDayMoment,
  {
    isToday = false,
    firstLogin,
    isTimeOfDayValid = () => true,
    isExpired = () => false,
  } = {},
) => {
  const isMultiSchedule = isMultiScheduleMetadata(instrument?.metadata?.schedule)
  const entries = getScheduleInstanceEntries(instrument)

  return entries.some(({ schedule, instances: entryInstances }) => {
    const instance = entryInstances?.[0]
    const visibilityConfig = isMultiSchedule
      ? getScheduleVisibilityConfig(schedule)
      : resolveInstrumentVisibilityConfig(instrument, instance) ||
        getInstrumentVisibilityConfig(instrument)

    if (
      visibilityConfig &&
      !isVisibleOnCalendarDay(visibilityConfig, targetDayMoment, { isToday })
    ) {
      return false
    }

    if (
      isToday &&
      visibilityConfig &&
      !isVisibleByConfig(visibilityConfig, moment())
    ) {
      return false
    }

    const instrumentForSchedule = { ...instrument, instances: entryInstances }
    const { deploy, expire, recurring } = schedule || {}
    if (!deploy) return false

    if (
      shouldHideNonPersistentPendingOnDay(instrumentForSchedule, targetDayMoment)
    ) {
      return false
    }

    const persistentOrVisit = shouldShowPersistentOrVisitInstrumentOnDay({
      instrument: instrumentForSchedule,
      schedule,
      targetDayMoment,
      isToday,
      isTimeOfDayValid,
      isExpired,
      firstLogin,
    })
    if (persistentOrVisit !== null) return persistentOrVisit

    const isRecurringInstrument =
      recurring?.scheme === 'n-months' ||
      recurring?.scheme === 'n-days' ||
      recurring?.scheme === 'n-weeks'
    const hasDaysOffset = deploy.hasOwnProperty('days_offset')
    const hasFirstLogin = deploy.hasOwnProperty('first_login')
    const isDeployOnParticipantStart =
      (hasDaysOffset && deploy.days_offset === 0) || hasFirstLogin
    const isPersistent = isPersistentRecurringScheme(recurring)
    const deploymentDay = getScheduleDeploymentDay(deploy, firstLogin)
    const areAllInstancesCompleted =
      entryInstances?.every((entryInstance) => entryInstance.completion) ?? false

    if (hasAbsoluteDeploy(deploy)) {
      const absoluteVisibility = isAbsoluteDeployScheduleVisibleOnDay(
        schedule,
        targetDayMoment,
        {
          isToday,
          isTimeOfDayValid,
          isExpired,
          firstLogin,
          areAllInstancesCompleted,
          instances: entryInstances,
        },
      )
      if (absoluteVisibility !== null) {
        return absoluteVisibility
      }
    }

    if (hasNowDeploy(deploy)) {
      const nowVisibility = isNowDeployScheduleVisibleOnDay(
        schedule,
        targetDayMoment,
        {
          isToday,
          isTimeOfDayValid,
          isExpired,
          firstLogin,
          areAllInstancesCompleted,
          instances: entryInstances,
        },
      )
      if (nowVisibility !== null) {
        return nowVisibility
      }
    }

    if (
      isDeployOnParticipantStart &&
      !isRecurringInstrument &&
      !isPersistent
    ) {
      // Use instance visibility window (start/visible_days/end) rather than
      // deploy-day-only. Deploy day still gates via getEffectiveInstanceCalendarStartDay.
      // When firstLogin is missing, fall through to instance start_date.
      const visibleViaInstance = (entryInstances || []).some(
        (entryInstance) =>
          !entryInstance.completion &&
          isInstanceVisibleOnCalendarDay(
            instrument,
            entryInstance,
            targetDayMoment,
            schedule,
            moment(),
            firstLogin,
          ),
      )

      if (visibleViaInstance) {
        if (
          isToday &&
          deploy.time_of_day &&
          !isTimeOfDayValid(deploy.time_of_day)
        ) {
          return false
        }
        return true
      }

      if (isToday && hasDaysOffset && deploy.days_offset === 0) {
        if (deploy.time_of_day && !isTimeOfDayValid(deploy.time_of_day)) {
          return false
        }
        return !areAllInstancesCompleted
      }

      return false
    }

    if (
      isDeployOnParticipantStart &&
      isRecurringInstrument &&
      recurring?.scheme === 'n-days'
    ) {
      if (deploymentDay) {
        const deploymentDayMoment = deploymentDay.clone().startOf('day')

        if (
          targetDayMoment.isSame(deploymentDayMoment, 'day') &&
          isToday &&
          deploy.time_of_day &&
          !isTimeOfDayValid(deploy.time_of_day)
        ) {
          return false
        }

        if (targetDayMoment.isBefore(deploymentDayMoment, 'day')) return false

        if (isExpired(expire, targetDayMoment, firstLogin)) return false

        if (areAllInstancesCompleted) return false

        const interval = recurring?.interval || 1
        if (interval === 1) return true

        const daysSinceDeployment = targetDayMoment.diff(
          deploymentDayMoment,
          'days',
        )
        return daysSinceDeployment >= 0 && daysSinceDeployment % interval === 0
      }

      if (isToday && hasDaysOffset && deploy.days_offset === 0) {
        return !areAllInstancesCompleted
      }
    }

    if (hasAbsoluteDeploy(deploy) || hasNowDeploy(deploy)) {
      return false
    }

    return (entryInstances || []).some(
      (entryInstance) =>
        !entryInstance.completion &&
        isInstanceVisibleOnCalendarDay(
          instrument,
          entryInstance,
          targetDayMoment,
          schedule,
          moment(),
          firstLogin,
        ),
    )
  })
}

const filterInstrumentsByVisibiity = instruments => {
  return instruments.filter(instrument => {
    return isInstrumentVisibleByTime(instrument)
  })
}

const isInstanceInInstrumentCatalog = (instrumentId, visibleInstruments) =>
  visibleInstruments.some((el) => el.instrument_id == instrumentId)

/** Visit/calendar-only instruments may exist in instances API without pending catalog entry. */
const hasDeployedCalendarInstances = (instanceGroup) => {
  const metadata = instanceGroup?.metadata
  const locations = metadata?.display_location || metadata?.placement || []

  return (
    locations.includes(INSTRUMENT_DISPLAY_LOCATION_OPTIONS.calendar) &&
    (instanceGroup?.instances || []).length > 0
  )
}

/**
 * Validates whether a chain-deployed instrument can be shown based on
 * completion of its dependency terms.
 * @param {Object} instrumentInstance - instrument instance or a fallback object with metadata
 * @param {Array} allInstances - array of all instrument instances to evaluate dependencies against
 * @returns {boolean}
 */
const isChainDeploymentSatisfied = (instrumentInstance, allInstances = []) => {
  const schedule = resolveSchedule(
    instrumentInstance?.metadata?.schedule,
    instrumentInstance?.schedule_id ||
    instrumentInstance?.instances?.[0]?.schedule_id,
  )
  const chainInfo = schedule?.chain_deployment_info

  if (!chainInfo?.has_chain_deployment) return true

  const { deployment_condition } = chainInfo
  const termsInstrumentIds =
    deployment_condition?.terms
      ?.map(({ instrument_id }) => instrument_id)
      .filter(Boolean) || []

  if (!termsInstrumentIds.length) return true

  const termsInstruments = allInstances.filter(item =>
    termsInstrumentIds.includes(item.instrument_id)
  )

  const completedInstrumentCount = termsInstruments.filter(instrument =>
    (instrument.instances || []).some(
      instance => instance?.completion !== null
    )
  ).length

  if (deployment_condition?.operator === 'OR') {
    return completedInstrumentCount > 0
  }

  // AND operator: all terms must be completed
  if (
    deployment_condition?.operator === 'AND' &&
    termsInstruments.length !== completedInstrumentCount
  ) {
    return false
  }

  // Default AND behaviour (when operator is not specified or is AND)
  return completedInstrumentCount === termsInstrumentIds.length
}

export const onFetchPendingInstruments = () => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: '/control/pending_instruments',
        headerOptions: {
          'hpi-device-type': 'web',
          'hpi-app-version': packageJson.version
        },
        hasLoader: true,
        success: json => {
          const validPendingInstruments = json.instruments.filter(
            isWebSupportedInstrument
          )
          if (validPendingInstruments.length > 0) {
            dispatch(sendInstrumentsDelivered(validPendingInstruments))
          }
          const visibleInstruments = filterInstrumentsByVisibiity(
            validPendingInstruments
          )
          if (visibleInstruments.length > 0) {
            dispatch(sendInstrumentsVisible(visibleInstruments))
          }
          dispatch(setPendingInstruments(visibleInstruments))
        },
        onFailLog: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.pendingInstrumentsFetchFailed,
            level: 'error',
            tags: {
              flow: 'instrument',
              action: 'pending_fetch_failed',
            },
          })
        },
      })
    )
  }
}

/**
 * Instrument architecture v1 when version is missing or < 2.
 */
export const isInstrumentArchitectureV1 = (user) => {
  const version = user?.study?.config?.instrument_architecture_version
  return !version || version < 2
}

/**
 * Fetch instruments using the endpoint for the study's instrument architecture:
 * - Arch 1: pending_instruments
 * - Arch 2+: /control/v2/participants/me/instruments
 * @param {boolean} noLoader - only applies to Arch 2+ fetch
 */
export const onFetchInstrumentsByArchitecture = (noLoader = false) => {
  return (dispatch, getState) => {
    const user = getState()?.user
    if (isInstrumentArchitectureV1(user)) {
      return dispatch(onFetchPendingInstruments())
    }
    return dispatch(onFetchInstruments(noLoader))
  }
}

/**
 * Filter persistent instruments
 * @param {Array} instruments
 * @returns
 */
const filterPersistInstruments = instruments =>
  instruments.filter(isPersistentInstrumentGroup)

/**
 * Maps instance groups to filtered instances by completion and date.
 * @param {Array} instancesList - list of instance groups (each has .instances)
 * @param {boolean} futureOnly - if true, keep start_date >= now; if false, start_date <= now
 * @param {boolean} excludeAnnouncement - if true, omit ANNOUNCEMENT instrument_type
 * @returns {Array}
 */
const filterInstancesByCompletionAndDate = (
  instancesList,
  futureOnly,
  excludeAnnouncement = false,
  confirmedVisitKeys,
) => {
  const currentDate = new Date()
  return instancesList
    .filter(
      ({ instrument_type }) =>
        !excludeAnnouncement || instrument_type !== 'ANNOUNCEMENT'
    )
    .map(instance => {
      const isPersistent = isPersistentInstrumentGroup(instance)
      const schedule = instance?.metadata?.schedule
      const newInstances = (instance.instances || [])
        .filter(({ completion }) => isPersistent || !completion)
        .filter((row) => {
          const { start_date } = row
          if (!start_date) {
            if (
              shouldHideUnconfirmedVisitInstrument(
                schedule,
                [row],
                confirmedVisitKeys,
              )
            ) {
              return false
            }
            return !futureOnly
          }
          const start = new Date(start_date)
          return futureOnly ? start >= currentDate : start <= currentDate
        })
      return { ...instance, instances: newInstances }
    })
}

/**
 * Fetch instruments for architecture v2+.
 * @param {boolean} noLoader
 * @returns
 */
export const onFetchInstruments = (noLoader = false) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: '/control/v2/participants/me/instruments',
        headerOptions: {
          'hpi-device-type': 'web',
          'hpi-app-version': packageJson.version
        },
        hasLoader: !noLoader,
        success: json => {
          // set announcements
          const announcements = json.instruments.filter(
            instrument => instrument.instrument_type === 'ANNOUNCEMENT'
          )

          const formattedAnnouncements = announcements.map(announcement => {
            return {
              ...announcement,
              title: announcement?.metadata?.announcement_content?.title,
              content: announcement?.metadata?.announcement_content?.content,
              id: announcement?.instrument_id
            }
          })

          dispatch(setAnnouncements({ announcements: formattedAnnouncements }))

          // set all instruments

          const validPendingInstruments = json.instruments.filter(
            isWebSupportedInstrument
          )
          dispatch(setAllInstruments(validPendingInstruments))

          const visibleInstruments = filterInstrumentsByVisibiity(
            validPendingInstruments
          )

          dispatch(onFetchInstrumentInstances(visibleInstruments, noLoader))
        },
        onFailLog: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.pendingInstrumentsFetchFailed,
            level: 'error',
            tags: {
              flow: 'instrument',
              action: 'pending_fetch_failed',
            },
          })
        },
      })
    )
  }
}

/**
 * Fetch Instruments instances for new Instruments Arch2++
 * @param {Array} visibleInstruments
 * @param {boolean} noLoader
 * @returns
 */
export const onFetchInstrumentInstances = (
  visibleInstruments,
  noLoader = false
) => {
  return (dispatch, getState) => {
    return dispatch(
      request({
        method: 'GET',
        url: '/control/v2/participants/me/instruments/instances',
        headerOptions: {
          'hpi-device-type': 'web',
          'hpi-app-version': packageJson.version
        },
        hasLoader: !noLoader,
        onFailLog: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.pendingInstrumentsFetchFailed,
            level: 'error',
            tags: {
              flow: 'instrument',
              action: 'instances_fetch_failed',
            },
          })
        },
        success: async json => {
          dispatch(loadingActions.startLoader(true))
          const firstLogin = getParticipantFirstLogin(getState()?.user)
          const studyId = getState().user?.study_id
          const confirmedVisitKeys = await dispatch(
            fetchConfirmedVisitKeySet(studyId),
          )
          dispatch(setConfirmedVisitKeys([...confirmedVisitKeys]))
          const instrumentInstanceArr = json?.instruments || []

          const filteredInstances = instrumentInstanceArr.filter(
            isWebSupportedInstrument
          )

          dispatch(setAllInstrumentsInstances(filteredInstances))

          // filter visible instances based on their respective Instrument’s Visibility ranges and visible_days
          const visibleInstances = instrumentInstanceArr
            .filter((instanceGroup) => {
              const { instrument_id, instrument_type } = instanceGroup

              if (instrument_type === 'ANNOUNCEMENT') return true
              if (isInstanceInInstrumentCatalog(instrument_id, visibleInstruments)) {
                return true
              }

              return hasDeployedCalendarInstances(instanceGroup)
            })
            .filter((instance, _, instancesArr) => {
              const isSatisfied = isChainDeploymentSatisfied(
                instance,
                instancesArr
              )

              if (!isSatisfied) return false

              if (
                shouldHideUnconfirmedVisitInstrument(
                  instance?.metadata?.schedule,
                  instance?.instances,
                  confirmedVisitKeys,
                )
              ) {
                return false
              }

              const currentDate = new Date()
              const isPersistent = isPersistentInstrumentGroup(instance)

              if (
                isPersistent &&
                (instance.instances || []).some((inst) =>
                  isInstanceActiveInCurrentWindow(
                    inst,
                    resolveSchedule(
                      instance?.metadata?.schedule,
                      inst.schedule_id || inst.scheduleId,
                    ),
                  ),
                )
              ) {
                return true
              }

              const instanceArr = (instance.instances || []).filter(
                ({ completion }) => !completion,
              )

              return instanceArr.some((inst) =>
                isWithinInstanceVisibilityWindow(instance, inst, currentDate),
              )
            })

          // get the instruments of the instance visibles
          const currentDate = new Date();

          const persistentInstruments = filterPersistInstruments(
            visibleInstruments
          ).filter(instrument => {
            const matchingInstance = filteredInstances.find(
              inst => inst.instrument_id === instrument.instrument_id
            )

            if (
              shouldHideUnconfirmedVisitInstrument(
                matchingInstance?.metadata?.schedule || instrument.metadata?.schedule,
                matchingInstance?.instances,
                confirmedVisitKeys,
              )
            ) {
              return false
            }

            // Use matching instance when available, otherwise fall back to instrument metadata
            return isChainDeploymentSatisfied(
              matchingInstance || { metadata: instrument.metadata },
              filteredInstances
            )
          })

          const persistentCalendarGroups = persistentInstruments.map(
            (instrument) => {
              const matchingInstance = filteredInstances.find(
                (inst) => inst.instrument_id === instrument.instrument_id,
              )
              const displayName =
                matchingInstance?.instrument_title ||
                instrument.instrument_title ||
                matchingInstance?.metadata?.display_name ||
                instrument.metadata?.display_name
              const translations =
                matchingInstance?.displayNameTranslations ||
                matchingInstance?.metadata?.display_name_translations ||
                instrument.metadata?.display_name_translations

              return {
                ...instrument,
                ...matchingInstance,
                instances:
                  matchingInstance?.instances || instrument.instances || [],
                instrument_title: displayName,
                displayNameTranslations: translations,
              }
            },
          )

          dispatch(
            setAllInstrumentsInstances(
              mergeInstances(filteredInstances, persistentCalendarGroups),
            ),
          )

          const visibleInstanceInstruments = visibleInstruments
            .filter(({ instrument_id }) => visibleInstances.some(el => el.instrument_id == instrument_id))
            .filter(instrument => {
              const matchingInstance = visibleInstances.find(
                inst => inst.instrument_id === instrument.instrument_id
              )
              if (!matchingInstance?.instances?.length) return false

              if (isMultiScheduleMetadata(instrument?.metadata?.schedule)) {
                return matchingInstance.instances.some((instance) => {
                  const schedule = resolveSchedule(
                    matchingInstance.metadata?.schedule,
                    instance.schedule_id || instance.scheduleId,
                  )

                  if (
                    !isInstanceVisibleByTime(instrument, instance, moment(currentDate))
                  ) {
                    return false
                  }

                  return isMultiScheduleInstanceActiveAt(
                    matchingInstance,
                    instance,
                    schedule,
                    firstLogin,
                    currentDate,
                  )
                })
              }

              return isInstrumentVisibleByTime(instrument)
            })
            .map(instrument => {
              const matchingInstance = visibleInstances.find(
                inst => inst.instrument_id === instrument.instrument_id
              )

              const activeInstances = (matchingInstance.instances || []).filter(
                instance => {
                  if (instance.completion) return false

                  const schedule = matchingInstance?.metadata?.schedule

                  if (!instance.start_date) {
                    return isInstanceActiveInCurrentWindow(instance, schedule)
                  }

                  if (!instance.end_date) return false

                  const start = new Date(instance.start_date)
                  const end = new Date(instance.end_date)

                  if (!(start <= currentDate && currentDate <= end)) {
                    return false
                  }

                  if (isMultiScheduleMetadata(instrument?.metadata?.schedule)) {
                    const schedule = resolveSchedule(
                      matchingInstance.metadata?.schedule,
                      instance.schedule_id || instance.scheduleId,
                    )

                    if (
                      !isInstanceVisibleByTime(
                        instrument,
                        instance,
                        moment(currentDate),
                      )
                    ) {
                      return false
                    }

                    return isMultiScheduleInstanceActiveAt(
                      matchingInstance,
                      instance,
                      schedule,
                      firstLogin,
                      currentDate,
                    )
                  }

                  return true
                }
              )

              return {
                ...instrument,
                instances: activeInstances
              }
            })

          // Expand multi-schedule instruments into one display row per active instance.
          // Legacy instruments (schedule object) remain a single row.
          const instrumentsForDisplay = expandInstrumentsByInstances([
            ...persistentInstruments,
            ...visibleInstanceInstruments
          ])

          // fire instrument_delivered event
          if (visibleInstances.length) {
            dispatch(
              sendEventForInstances(
                INSTRUMENT_EVENTS_MAP.delivered,
                visibleInstances
              )
            )
          }

          dispatch(setInstruments(instrumentsForDisplay))

          const calendarVisibleInstances = mergeInstances(
            visibleInstances,
            persistentCalendarGroups,
          )

          // set future instances (exclude ANNOUNCEMENT, keep start_date >= now)
          const futureInstances = filterInstancesByCompletionAndDate(
            calendarVisibleInstances,
            true,
            true,
            confirmedVisitKeys,
          )
          dispatch(setFutureInstances(futureInstances))

          // set filtered instances (keep start_date <= now) for visible events and store
          const visibleFilteredInstances = filterInstancesByCompletionAndDate(
            calendarVisibleInstances,
            false,
            false,
            confirmedVisitKeys,
          )

          if (visibleFilteredInstances.length) {
            const instancesWithIds = visibleFilteredInstances.filter(
              (group) => group?.instances?.[0]?.instance_id,
            )
            if (instancesWithIds.length) {
              dispatch(
                sendEventForInstances(
                  INSTRUMENT_EVENTS_MAP.visible,
                  instancesWithIds,
                ),
              )
            }
          }

          dispatch(setInstrumentsInstances(visibleFilteredInstances))

          dispatch(loadingActions.stopLoader(true))
        }
      })
    )
  }
}

export const onFetchParticipantInstrumentInstances = ptpId => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: `/control/v2/participants/${ptpId}/instruments/instances`,
        headerOptions: {
          'hpi-device-type': 'web',
          'hpi-app-version': packageJson.version
        },
        hasLoader: true,
        success: json => {
          dispatch(setParticipantInstrumentsInstances(json.instruments))
        }
      })
    )
  }
}

const defaultList = [
  {
    identity: 'no-pending-instrumets',
    last_updated: '',
    title: 'No Pending Instruments!',
    display_icon: 'GENERAL',
    est_duration: 'SHORT',
    gamification_points: 0,
    version: 1,
    attribution_text: '',
    language: 'en',
    questions: [],
    url: '/',
    type: 'CONGRATS'
  }
]

const formatUrl = url => {
  const sliceIndex = url.indexOf('/studies')
  const subUrlString = url.slice(sliceIndex)
  const newUrl = `/control${subUrlString}`
  return newUrl
}

const sortInstrumentsByPriority = list => {
  return list.sort((instrument1, instrument2) => {
    return instrument2.priority - instrument1.priority
  })
}

const formatInstrumentsList = (list = defaultList) => {
  let filteredList
  if (list.length > 0) {
    filteredList = list.filter(isWebSupportedInstrument)
    return sortInstrumentsByPriority(
      filteredList.map(
        ({
          metadata,
          url,
          instrument_type,
          instrument_id,
          last_updated,
          instances,
          schedule_id: topLevelScheduleId,
          display_key,
          instruments_deployment_queue_id: depQId
        }) => {
          const {
            display_icon,
            est_duration,
            display_name,
            gamification_points,
            version,
            attribution_text = '',
            priority,
            display_name_translations: displayNameTranslations,
            display_location,
            schedule,
            review_screen,
            visibility,
            visibilityRanges
          } = metadata
          const instanceScheduleId =
            topLevelScheduleId ||
            instances?.[0]?.schedule_id ||
            instances?.[0]?.scheduleId
          const resolvedSchedule = resolveSchedule(schedule, instanceScheduleId)
          const scheduleScopedVisibility =
            isMultiScheduleMetadata(schedule) ||
            topLevelScheduleId ||
            instances?.[0]?.schedule_id ||
            instances?.[0]?.scheduleId
          return {
            depQId,
            displayNameTranslations,
            identity: instrument_id,
            displayKey:
              display_key ||
              `${instrument_id}:${instanceScheduleId || 'default'}:${instances?.[0]?.instance_id || 'none'}`,
            schedule_id: instanceScheduleId || resolvedSchedule?.scheduleId || resolvedSchedule?.schedule_id,
            last_updated,
            instances,
            title: display_name,
            display_icon: display_icon.toUpperCase(),
            est_duration: est_duration.toUpperCase(),
            gamification_points: parseInt(gamification_points, 10),
            version,
            attribution_text,
            language: 'en',
            questions: [],
            url: formatUrl(url),
            type: instrument_type,
            priority,
            displayLocation: display_location,
            schedule: resolvedSchedule,
            review_screen,
            visibility: scheduleScopedVisibility
              ? normalizeVisibilityConfig({
                  visibility: resolvedSchedule?.visibility,
                })
              : normalizeVisibilityConfig({ visibility, visibilityRanges })
          }
        }
      )
    )
  }
  return sortInstrumentsByPriority(list)
}

const pendingInstrumentsList = (state = [], action) => {
  switch (action.type) {
    case SET_PENDING_INSTRUMENTS:
      return formatInstrumentsList(action.instruments)
    default:
      return state
  }
}

const instrumentsList = (state = [], action) => {
  switch (action.type) {
    case SET_INSTRUMENTS:
      return formatInstrumentsList(action.instruments)
    default:
      return state
  }
}

const allInstrumentsList = (state = [], action) => {
  switch (action.type) {
    case SET_ALL_INSTRUMENTS:
      return formatInstrumentsList(action.instruments)
    default:
      return state
  }
}

const allInstancesList = (state = [], action) => {
  switch (action.type) {
    case SET_ALL_INSTRUMENTS_INSTANCES:
      return action.instances
    default:
      return state
  }
}

const instancesList = (state = [], action) => {
  switch (action.type) {
    case SET_INSTRUMENTS_INSTANCES:
      return action.instances
    default:
      return state
  }
}

const participantInstancesList = (state = [], action) => {
  switch (action.type) {
    case SET_PARTICIPANT_INSTRUMENTS_INSTANCES:
      return action.instances
    default:
      return state
  }
}

const futureInstanceList = (state = [], action) => {
  switch (action.type) {
    case SET_FUTURE_INSTANCES:
      return action.instances
    default:
      return state
  }
}

const confirmedVisitKeys = (state = [], action) => {
  switch (action.type) {
    case SET_CONFIRMED_VISIT_KEYS:
      return action.keys || []
    default:
      return state
  }
}

export const instrumentActions = {
  onFetchPendingInstruments,
  onFetchInstruments,
  onFetchInstrumentsByArchitecture,
}

export default combineReducers({
  pendingInstrumentsList,
  instrumentsList,
  allInstrumentsList,
  allInstancesList,
  instancesList,
  futureInstanceList,
  participantInstancesList,
  confirmedVisitKeys
})
